import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, UserNameCell } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { OvertimeRecord } from '../../types/overtime.types';
import { formatSecondsAsHoursMinutes } from '../../utils/format-duration';

interface CreateOvertimeTableColumnsProps {
  t: (key: string) => string;
  onPayoff: (overtime: OvertimeRecord) => void;
  /** Holds `hrm::time-attendance::overtime-payoff` in the active organization. */
  canPayoff: boolean;
}

export const createOvertimeTableColumns = ({
  t,
  onPayoff,
  canPayoff,
}: CreateOvertimeTableColumnsProps): ColumnDef<OvertimeRecord>[] => [
  {
    id: 'ResourceName',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('EMPLOYEE_NAME')} />,
    accessorFn: (row) => row.ResourceName,
    cell: ({ row }) => (
      <UserNameCell name={row.original.ResourceName} email={row.original.ResourceEmail} />
    ),
  },
  {
    id: 'TotalTakenOvertimeHourInSecond',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('TAKEN_OVERTIME')} />,
    accessorFn: (row) => row.TotalTakenOvertimeHourInSecond,
    cell: ({ row }) => (
      <span>{formatSecondsAsHoursMinutes(row.original.TotalTakenOvertimeHourInSecond)}</span>
    ),
  },
  {
    id: 'TotalRequestedOvertimeHourInSecond',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('REQUESTED_OVERTIME')} />
    ),
    accessorFn: (row) => row.TotalRequestedOvertimeHourInSecond,
    cell: ({ row }) => (
      <span>{formatSecondsAsHoursMinutes(row.original.TotalRequestedOvertimeHourInSecond)}</span>
    ),
  },
  {
    id: 'TotalRemainingOvertimeHourInSecond',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('REMAINING_OVERTIME')} />
    ),
    accessorFn: (row) => row.TotalRemainingOvertimeHourInSecond,
    cell: ({ row }) => (
      <span>{formatSecondsAsHoursMinutes(row.original.TotalRemainingOvertimeHourInSecond)}</span>
    ),
  },
  {
    id: 'actions',
    // Card: top right, next to the employee.
    meta: { mobile: 'badge' },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }) =>
      canPayoff && row.original.TotalRemainingOvertimeHourInSecond >= 60 ? (
        <Button
          variant="outline"
          size="sm"
          onClick={(event) => {
            // The row itself opens the employee's details.
            event.stopPropagation();
            onPayoff(row.original);
          }}
        >
          {t('PAY_OFF')}
        </Button>
      ) : null,
  },
];
